/**
 * MobileAwareLayout — wraps children in MobileLayout on mobile,
 * or AppHeader + app layout on desktop.
 *
 * Set `bare` for pages that should NOT get the bottom nav bar on mobile
 * (auth pages, landing page). On desktop, `bare` suppresses AppHeader.
 *
 * Must be used inside a Router context (useIsMobile reads query params).
 */

import type { ReactNode } from 'react';
import { useIsMobile } from '../hooks/useIsMobile';
import { MobileLayout } from './MobileLayout/MobileLayout';
import { AppHeader } from './AppHeader';

interface MobileAwareLayoutProps {
  children: ReactNode;
  /** Extra class on desktop <main> (e.g. app-main--with-sidebar) */
  mainClass?: string;
  /** If true, suppresses desktop AppHeader AND mobile bottom nav bar */
  bare?: boolean;
}

export function MobileAwareLayout({ children, mainClass, bare }: Readonly<MobileAwareLayoutProps>) {
  const isMobile = useIsMobile();

  if (isMobile) {
    if (bare) return <>{children}</>;
    return <MobileLayout>{children}</MobileLayout>;
  }

  if (bare) {
    return <>{children}</>;
  }

  return (
    <div className="app">
      <AppHeader />
      <main className={`app-main${mainClass ? ` ${mainClass}` : ''}`}>
        {children}
      </main>
    </div>
  );
}
